import React, { useState } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { LectureDownloader } from '@/components/LectureDownloader';
import { 
  CheckCircle2, 
  FileText,
  Play,
  ChevronLeft, 
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Custom components for the lecture
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r">
      <div className="flex items-start">
        <CheckCircle2 className="h-6 w-6 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-blue-900">{children}</div>
      </div>
    </div>
  );
};

const ScientificDiagram = ({ 
  title, 
  caption, 
  children 
}: { 
  title: string; 
  caption: string; 
  children: React.ReactNode;
}) => {
  return (
    <figure className="my-8 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">{caption}</p>
      </div>
      <div className="overflow-hidden">
        {children}
      </div>
    </figure>
  );
};

const VideoPlaceholder = () => {
  return (
    <div className="w-full aspect-video bg-gray-100 rounded-lg flex items-center justify-center mb-8">
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Play className="h-8 w-8 text-white" />
        </div>
        <p className="text-gray-600 font-medium">Lecture video will be available after the live session</p>
      </div>
    </div>
  );
};

// Slideshow component
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
}

const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };
  
  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  return (
    <div className="mb-8">
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-medium">Lecture Slideshow</h3>
        <div className="flex gap-2">
          <button
            onClick={goToPrevSlide}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-sm text-gray-500">
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={goToNextSlide}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-medium">{slides[currentSlide].title}</h2>
        </div>
        <div className="p-6">
          {slides[currentSlide].content}
        </div>
      </div>
    </div>
  );
};

const HormonesCellularFundamentals: React.FC = () => {


  const resources = [
    {
      title: 'Lecture Notes',
      link: '#',
      icon: <FileText size={16} />
    },
    {
      title: 'References',
      link: '#',
      icon: <FileText size={16} />
    }
  ];

  const lectureSlides = [
    {
      id: 1,
      title: "Introduction to Hormones",
      content: (
        <>
          <p>Hormones are chemical messengers that regulate numerous physiological processes in the body. They are produced by specific glands and travel through the bloodstream to target tissues, where they elicit precise cellular responses. Understanding the molecular mechanisms of hormone action is crucial for appreciating their role in both health and disease.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Definition and Scope</h3>
              <p>Hormones are signaling molecules that are synthesized and secreted by specialized cells, typically in endocrine glands, and transported via the circulatory system to target cells where they induce specific physiological responses. They regulate metabolism, growth, development, reproduction, and numerous other biological processes.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Historical Context</h3>
              <p>The concept of "internal secretions" was first proposed in the 19th century, but the term "hormone" (from the Greek word meaning "to excite" or "set in motion") was coined in 1905 by Ernest Starling. Since then, our understanding of hormone action has evolved dramatically, from simple humoral concepts to intricate signal transduction networks.</p>
            </div>
          </div>
          
          <ScientificDiagram 
            title="1. Hormone Classification and General Mechanisms" 
            caption="Overview of the three main classes of hormones (peptide/protein, steroid, and amine hormones) and their general mechanisms of action. The diagram illustrates the synthesis, secretion, transport, and signaling pathways for different hormone types, highlighting key cellular components involved in hormone response."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Hormone Classification and General Mechanisms</text>
                
                {/* A. Peptide Hormones */}
                <g transform="translate(200, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. Peptide/Protein Hormones</text>
                  
                  {/* Cell with components */}
                  <rect x="-100" y="-30" width="200" height="140" rx="5" fill="#F8F9F9" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Cell membrane */}
                  <rect x="-100" y="-30" width="200" height="10" rx="2" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1" />
                  
                  {/* Receptor */}
                  <rect x="-80" y="-30" width="20" height="40" rx="2" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-70" y="-5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">R</text>
                  
                  {/* G-protein */}
                  <circle cx="-70" cy="25" r="10" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="-70" y="28" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#F1C40F">G</text>
                  
                  {/* Adenylyl cyclase */}
                  <rect x="-40" y="15" width="20" height="20" rx="2" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="-30" y="28" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#F1C40F">AC</text>
                  
                  {/* Second messenger */}
                  <circle cx="0" cy="25" r="15" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="28" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">cAMP</text>
                  
                  {/* Kinase cascade */}
                  <rect x="40" y="15" width="20" height="20" rx="2" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="50" y="28" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">PKA</text>
                  
                  {/* Nucleus */}
                  <circle cx="0" cy="80" r="25" fill="#F8F9F9" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="83" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Nucleus</text>
                  
                  {/* Connection arrows */}
                  <path d="M-60,25 L-40,25" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
                  <path d="M-20,25 L-15,25" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M15,25 L40,25" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M50,35 L30,65" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  
                  {/* Examples */}
                  <text x="0" y="120" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Examples: Insulin, GH, PTH</text>
                </g>
                
                {/* B. Steroid Hormones */}
                <g transform="translate(600, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. Steroid Hormones</text>
                  
                  {/* Cell with components */}
                  <rect x="-100" y="-30" width="200" height="140" rx="5" fill="#F8F9F9" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Cell membrane */}
                  <rect x="-100" y="-30" width="200" height="10" rx="2" fill="#AED6F1" stroke="#3498DB" strokeWidth="1" />
                  
                  {/* Steroid hormone */}
                  <path d="M-80,-15 L-70,-10 L-60,-15 L-50,-10 L-50,-20 L-60,-25 L-70,-20 L-80,-25 Z" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-65" y="-5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">S</text>
                  
                  {/* Cytosolic receptor */}
                  <circle cx="0" cy="20" r="15" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="23" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">SR</text>
                  
                  {/* Nucleus */}
                  <circle cx="0" cy="80" r="25" fill="#F8F9F9" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="75" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Nucleus</text>
                  <text x="0" y="90" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">DNA</text>
                  
                  {/* Hormone-receptor complex */}
                  <circle cx="0" cy="55" r="10" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="58" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">S-R</text>
                  
                  {/* Connection arrows */}
                  <path d="M-50,-15 L-15,15" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M0,35 L0,45" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  
                  {/* Examples */}
                  <text x="0" y="120" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Examples: Cortisol, Estrogen</text>
                </g>
                
                {/* C. Amine Hormones */}
                <g transform="translate(400, 360)">
                  {/* Background */}
                  <rect x="-320" y="-50" width="640" height="120" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111">C. Amine Hormones</text>
                  
                  {/* Cell diagram */}
                  <g transform="translate(-150, 20)">
                    <circle cx="0" cy="0" r="30" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="-10" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#E74C3C">Membrane</text>
                    <text x="0" y="7" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#E74C3C">Receptor</text>
                    <text x="0" y="45" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#E74C3C">Epinephrine, T3/T4</text>
                  </g>
                  
                  {/* GPCR model */}
                  <g transform="translate(150, 20)">
                    <rect x="-100" y="-30" width="200" height="60" rx="5" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#E74C3C">Mix of Mechanisms</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="10" fill="#E74C3C">(Some act via GPCRs, some via</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#E74C3C">nuclear receptors)</text>
                  </g>
                </g>
                
                {/* Connecting arrows */}
                <path d="M200,210 L300,310" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M600,210 L500,310" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#196F3D" />
                  </marker>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                  <marker id="yellowArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#F1C40F" />
                  </marker>
                  <marker id="redArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p>Hormones are traditionally classified into three major categories based on their chemical structure: peptide/protein hormones, steroid hormones, and amine hormones. Each category exhibits distinct characteristics in terms of synthesis, transport, mechanism of action, and temporal dynamics. Understanding these differences is essential for appreciating how hormones exert their wide-ranging effects on physiology and behavior.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Peptide/Protein Hormones</h3>
              <p>These water-soluble messengers (e.g., insulin, growth hormone, thyroid-stimulating hormone) are synthesized as preprohormones and undergo post-translational processing. They cannot cross cell membranes due to their hydrophilic nature, so they bind to cell surface receptors to initiate intracellular signaling cascades.</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Steroid Hormones</h3>
              <p>Derived from cholesterol, these lipid-soluble hormones (e.g., cortisol, estrogen, testosterone) can diffuse across plasma membranes. They typically bind to intracellular receptors that function as transcription factors, directly regulating gene expression. They circulate bound to carrier proteins, which extends their half-life.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Amine Hormones</h3>
              <p>Derived from amino acids (particularly tyrosine), these include thyroid hormones (T3, T4) and catecholamines (epinephrine, norepinephrine). Their mechanisms vary: catecholamines bind to membrane receptors (like GPCRs), while thyroid hormones primarily act through nuclear receptors, similar to steroid hormones.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The body's endocrine system comprises a complex network of glands and hormones that maintain homeostasis through exquisitely sensitive feedback mechanisms. Each hormone class utilizes specific cellular pathways to transmit their signals, with effects that can range from immediate (seconds to minutes) to prolonged (hours to days). The specificity of hormone action is determined by receptor distribution, with target cells expressing receptors for only the hormones they are designed to respond to.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 2,
      title: "Hormone Synthesis, Release, and Transport",
      content: (
        <>
          <p>Hormones are synthesized by specialized endocrine cells and released into the bloodstream in response to specific stimuli. The production and secretion of hormones involve complex cellular mechanisms that ensure precise control over their availability in the circulation. Once released, hormones must reach their target tissues in a biologically active form, which often involves interactions with transport proteins and metabolism by various tissues.</p>
          
          <ScientificDiagram 
            title="2. Hormone Synthesis and Release Mechanisms" 
            caption="The processes of hormone synthesis, storage, and release from endocrine cells. A) Peptide hormone synthesis pathway through the endoplasmic reticulum and Golgi apparatus. B) Steroid hormone synthesis pathway from cholesterol in specialized organelles. C) Regulatory mechanisms controlling hormone release into the bloodstream."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Hormone Synthesis and Release Mechanisms</text>
                
                {/* A. Peptide Hormone Synthesis */}
                <g transform="translate(200, 140)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. Peptide Hormone Synthesis</text>
                  
                  {/* Nucleus */}
                  <circle cx="0" cy="-20" r="25" fill="#F8F9F9" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="-17" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Nucleus</text>
                  
                  {/* mRNA */}
                  <path d="M25,-20 Q40,-10 50,0 Q60,10 45,20" stroke="#8E44AD" strokeWidth="2" fill="none" />
                  <text x="52" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">mRNA</text>
                  
                  {/* ER */}
                  <rect x="-70" y="20" width="60" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-40" y="40" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#8E44AD">ER</text>
                  
                  {/* Ribosome */}
                  <circle cx="0" cy="20" r="10" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="0" y="23" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#F1C40F">R</text>
                  
                  {/* Golgi */}
                  <rect x="30" y="20" width="50" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="55" y="40" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#8E44AD">Golgi</text>
                  
                  {/* Secretory vesicle */}
                  <circle cx="0" cy="80" r="15" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="84" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#8E44AD">Vesicle</text>
                  
                  {/* Cell membrane */}
                  <rect x="-100" y="110" width="200" height="10" rx="2" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1" />
                  
                  {/* Arrows */}
                  <path d="M20,-20 L30,-20" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  <path d="M10,20 L30,20" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  <path d="M55,50 L20,75" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  <path d="M0,95 L0,110" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  
                  {/* Bloodstream */}
                  <text x="0" y="140" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Bloodstream</text>
                </g>
                
                {/* B. Steroid Hormone Synthesis */}
                <g transform="translate(600, 140)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. Steroid Hormone Synthesis</text>
                  
                  {/* Cholesterol */}
                  <rect x="-80" y="-20" width="60" height="20" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-50" y="-5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Cholesterol</text>
                  
                  {/* Mitochondrion */}
                  <ellipse cx="-20" cy="40" rx="30" ry="20" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="-20" y="44" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#27AE60">Mitochondrion</text>
                  
                  {/* ER */}
                  <rect x="30" y="30" width="60" height="20" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="60" y="44" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">SER</text>
                  
                  {/* Steroid hormone */}
                  <path d="M50,70 L60,75 L70,70 L80,75 L80,65 L70,60 L60,65 L50,60 Z" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="65" y="90" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Steroid</text>
                  
                  {/* Cell membrane */}
                  <rect x="-100" y="110" width="200" height="10" rx="2" fill="#AED6F1" stroke="#3498DB" strokeWidth="1" />
                  
                  {/* Arrows */}
                  <path d="M-20,-10 L-20,20" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M10,40 L30,40" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M60,50 L60,60" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M65,90 L0,110" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  
                  {/* Bloodstream */}
                  <text x="0" y="140" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Bloodstream</text>
                </g>
                
                {/* C. Hormone Release Regulation */}
                <g transform="translate(400, 360)">
                  {/* Background */}
                  <rect x="-320" y="-50" width="640" height="140" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111">C. Hormone Release Regulation</text>
                  
                  {/* Neural Stimuli */}
                  <g transform="translate(-200, 30)">
                    <rect x="-80" y="-20" width="160" height="40" rx="5" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="-2" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#E74C3C">Neural Stimuli</text>
                    <text x="0" y="16" textAnchor="middle" fontSize="10" fill="#E74C3C">(e.g., nerve impulses)</text>
                  </g>
                  
                  {/* Humoral Stimuli */}
                  <g transform="translate(0, 30)">
                    <rect x="-80" y="-20" width="160" height="40" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                    <text x="0" y="-2" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#F1C40F">Humoral Stimuli</text>
                    <text x="0" y="16" textAnchor="middle" fontSize="10" fill="#F1C40F">(e.g., blood glucose levels)</text>
                  </g>
                  
                  {/* Hormonal Stimuli */}
                  <g transform="translate(200, 30)">
                    <rect x="-80" y="-20" width="160" height="40" rx="5" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="-2" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#27AE60">Hormonal Stimuli</text>
                    <text x="0" y="16" textAnchor="middle" fontSize="10" fill="#27AE60">(e.g., tropic hormones)</text>
                  </g>
                  
                  {/* Feedback loop */}
                  <path d="M-220,50 C-220,80 220,80 220,50" stroke="#333" strokeWidth="2" strokeDasharray="5,3" />
                  <text x="0" y="70" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Feedback Loops</text>
                </g>
                
                {/* Connecting arrows */}
                <path d="M200,210 L300,310" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M600,210 L500,310" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60" />
                  </marker>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                  <marker id="yellowArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#F1C40F" />
                  </marker>
                  <marker id="redArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Stimuli for Hormone Release</h3>
              <p>Hormone release can be triggered by various stimuli depending on the gland and physiological context. <strong>Humoral stimuli</strong> involve changes in blood chemistry, such as increased glucose levels stimulating insulin secretion from pancreatic β-cells or decreased calcium levels prompting parathyroid hormone release. <strong>Neural stimuli</strong> include direct innervation of endocrine glands, as seen in the adrenal medulla's response to sympathetic nervous system activation. <strong>Hormonal stimuli</strong> occur in hierarchical endocrine systems, where tropic hormones from the pituitary stimulate hormone production in target glands.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Hormone Transport and Bioavailability</h3>
              <p>Once released into circulation, hormones must reach their target tissues in a bioactive form. Water-soluble hormones (peptides and catecholamines) generally circulate freely in plasma and have relatively short half-lives. In contrast, lipid-soluble hormones (steroids, thyroid hormones) are hydrophobic and require specialized transport proteins such as thyroxine-binding globulin (TBG), sex hormone-binding globulin (SHBG), or corticosteroid-binding globulin (CBG). These binding proteins extend hormone half-life and create a reservoir in circulation, but only the unbound, "free" fraction is bioactive.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Hormone synthesis and release are tightly regulated processes that respond to specific physiological signals. Most endocrine systems are governed by negative feedback loops that maintain homeostasis, where the hormone's effects ultimately suppress its own production. For example, thyroid hormones (T3/T4) inhibit the release of thyroid-stimulating hormone (TSH) from the pituitary and thyrotropin-releasing hormone (TRH) from the hypothalamus. This feedback mechanism ensures hormone levels remain within an optimal physiological range.
          </KeyTakeaway>
          
          <p>The transport of hormones in circulation affects their bioavailability and clearance rates. While binding proteins protect hormones from rapid degradation and excretion, they also regulate the amount of free hormone available to engage with target tissue receptors. Alterations in binding protein levels (e.g., due to liver disease, pregnancy, or oral contraceptive use) can significantly affect hormone bioavailability without changing the total hormone concentration, which has important implications for laboratory assessment and clinical interpretation of hormone levels.</p>
        </>
      )
    },
    {
      id: 3,
      title: "Hormone Receptors and Signaling Pathways",
      content: (
        <>
          <p>Hormones exert their biological effects by binding to specific receptors on or within target cells, initiating complex signaling cascades that ultimately alter cellular function. The location of these receptors and the nature of the signaling pathways they activate depend largely on the chemical properties of the hormone and determine both the speed and duration of the hormonal response.</p>
          
          <ScientificDiagram 
            title="3. Major Hormone Receptor Types and Signaling Mechanisms" 
            caption="Detailed illustration of the primary hormone receptor families and their downstream signaling cascades. A) G-protein coupled receptors (GPCRs) and their second messenger systems. B) Nuclear receptors and their genomic effects. C) Receptor tyrosine kinases (RTKs) and their kinase cascades."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Major Hormone Receptor Types and Signaling Mechanisms</text>
                
                {/* A. G-Protein Coupled Receptors */}
                <g transform="translate(200, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. G-Protein Coupled Receptors</text>
                  
                  {/* Cell membrane */}
                  <rect x="-100" y="-10" width="200" height="20" rx="2" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* GPCR */}
                  <path d="M-50,-30 L-50,10 Q-40,25 -30,10 Q-20,25 -10,10 Q0,25 10,10 L10,-30 Z" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-20" y="-15" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">GPCR</text>
                  
                  {/* Hormone binding */}
                  <circle cx="-40" cy="-45" r="10" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="-40" y="-42" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#F1C40F">H</text>
                  <path d="M-40,-35 L-40,-25" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
                  
                  {/* G-protein signaling */}
                  <circle cx="-30" cy="25" r="10" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="-30" y="28" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#F1C40F">G</text>
                  
                  {/* Effector enzyme */}
                  <rect x="10" y="20" width="20" height="20" rx="2" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="20" y="33" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">AC</text>
                  
                  {/* Second messenger */}
                  <circle cx="60" y="25" r="15" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="60" y="18" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">cAMP</text>
                  <text x="60" y="32" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">IP₃/DAG</text>
                  
                  {/* Downstream effects */}
                  <rect x="20" y="60" width="60" height="20" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="50" y="74" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">PKA/PKC</text>
                  
                  {/* Downstream arrow */}
                  <path d="M50,80 L50,95" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  <text x="50" y="115" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Rapid Effects</text>
                  
                  {/* Connection arrows */}
                  <path d="M-20,25 L10,25" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M30,25 L45,25" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M60,40 L50,60" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                </g>
                
                {/* B. Nuclear Receptors */}
                <g transform="translate(600, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. Nuclear Receptors</text>
                  
                  {/* Cell membrane */}
                  <rect x="-100" y="-10" width="200" height="10" rx="2" fill="#AED6F1" stroke="#3498DB" strokeWidth="1" />
                  
                  {/* Steroid hormone */}
                  <path d="M-80,-40 L-70,-35 L-60,-40 L-50,-35 L-50,-45 L-60,-50 L-70,-45 L-80,-50 Z" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-65" y="-25" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">SH</text>
                  
                  {/* Cytosolic receptor */}
                  <circle cx="0" cy="30" r="15" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="34" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">NR</text>
                  
                  {/* Hormone-receptor complex */}
                  <rect x="-15" y="60" width="30" height="15" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="71" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">SH-NR</text>
                  
                  {/* Nucleus */}
                  <ellipse cx="0" cy="100" rx="40" ry="25" fill="#f9f9f9" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="95" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Nucleus</text>
                  <path d="M-20,105 L20,105" stroke="#3498DB" strokeWidth="1" />
                  <text x="0" y="115" textAnchor="middle" fontSize="10" fill="#3498DB">DNA</text>
                  
                  {/* Arrows */}
                  <path d="M-50,-40 L-15,25" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M0,45 L0,60" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M0,75 L0,85" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M-10,125 L-10,145" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  
                  {/* Gene transcription */}
                  <rect x="-50" y="145" width="100" height="20" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="160" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Gene Transcription</text>
                </g>
                
                {/* C. Receptor Tyrosine Kinases */}
                <g transform="translate(400, 360)">
                  {/* Background */}
                  <rect x="-320" y="-50" width="640" height="150" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111">C. Receptor Tyrosine Kinases (RTKs)</text>
                  
                  {/* Cell membrane */}
                  <rect x="-300" y="0" width="600" height="10" rx="2" fill="#f9f9f9" stroke="#333" strokeWidth="1" />
                  
                  {/* RTK signaling */}
                  <g transform="translate(-150, 0)">
                    {/* Receptor */}
                    <rect x="-20" y="-30" width="10" height="30" rx="2" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    <rect x="10" y="-30" width="10" height="30" rx="2" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="-45" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#E74C3C">RTK</text>
                    
                    {/* Hormone binding */}
                    <circle cx="-15" cy="-50" r="8" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="-15" y="-47" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">H</text>
                    
                    {/* Dimerization */}
                    <circle cx="15" cy="-50" r="8" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="15" y="-47" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">H</text>
                    
                    {/* Kinase cascade */}
                    <path d="M0,10 L0,30 L-20,50 L-20,70" stroke="#E74C3C" strokeWidth="1.5" markerEnd="url(#redArrow)" />
                    <path d="M0,30 L20,50 L20,70" stroke="#E74C3C" strokeWidth="1.5" markerEnd="url(#redArrow)" />
                    
                    <rect x="-30" y="70" width="20" height="15" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1" />
                    <text x="-20" y="81" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">PI3K</text>
                    
                    <rect x="10" y="70" width="20" height="15" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1" />
                    <text x="20" y="81" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">RAS</text>
                  </g>
                  
                  {/* Downstream pathways */}
                  <g transform="translate(150, 40)">
                    <rect x="-100" y="0" width="200" height="40" rx="5" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                    <text x="0" y="18" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#333">Multiple Signaling Pathways</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#333">(PI3K-AKT, RAS-MAPK, JAK-STAT, etc.)</text>
                  </g>
                </g>
                
                {/* Connecting arrows */}
                <path d="M200,210 L300,310" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M600,210 L500,310" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60" />
                  </marker>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                  <marker id="yellowArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#F1C40F" />
                  </marker>
                  <marker id="redArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">G-Protein Coupled Receptors (GPCRs)</h3>
              <p>GPCRs are seven-transmembrane domain proteins that constitute the largest receptor family in humans. Upon hormone binding, they activate associated G-proteins, which modulate effector enzymes or ion channels to generate second messengers. Different G-protein subtypes (Gs, Gi, Gq, G12/13) activate distinct pathways: Gs stimulates adenylyl cyclase to produce cAMP, Gi inhibits adenylyl cyclase, Gq activates phospholipase C to generate IP₃ and DAG, and G12/13 regulates small GTPases.</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Nuclear Receptors</h3>
              <p>Nuclear receptors are ligand-activated transcription factors that directly regulate gene expression. They include steroid hormone receptors (for cortisol, estrogen, testosterone) and non-steroid receptors (for thyroid hormones, vitamin D). Upon binding their lipophilic ligands, these receptors undergo conformational changes, dimerize, and bind to specific DNA sequences called hormone response elements (HREs), recruiting co-activators or co-repressors to modulate transcription.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Receptor Tyrosine Kinases (RTKs)</h3>
              <p>RTKs, exemplified by the insulin receptor, have an extracellular hormone-binding domain and an intracellular tyrosine kinase domain. Hormone binding induces receptor dimerization and autophosphorylation, creating docking sites for signaling proteins containing SH2 or PTB domains. This activates downstream pathways like PI3K–AKT (metabolic effects) and RAS–MAPK (mitogenic effects), leading to diverse cellular responses.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The diversity of hormone receptor types and signaling pathways allows for exquisite regulation of cellular responses in both temporal and spatial dimensions. GPCRs and RTKs typically mediate rapid responses (seconds to minutes) through protein phosphorylation cascades and second messengers, while nuclear receptors orchestrate slower but more sustained responses (hours to days) through transcriptional regulation. Signal amplification, cross-talk between pathways, and modulation by cellular context add further layers of complexity to hormone signaling networks.
          </KeyTakeaway>
          
          <p>Many hormones can act through multiple receptor types and signaling pathways, allowing for diverse and context-dependent effects in different target tissues. For example, estrogen exerts both genomic effects through nuclear estrogen receptors (ERα and ERβ) and rapid non-genomic effects through membrane-associated estrogen receptors. Similarly, thyroid hormones primarily act through nuclear receptors but can also initiate non-genomic actions at the plasma membrane, mitochondria, and cytoskeleton. This signaling versatility contributes to the pleiotropic effects of many hormones across different tissues and physiological states.</p>
        </>
      )
    },
    {
      id: 4,
      title: "Metabolic and Redox Influences on Hormone Action",
      content: (
        <>
          <p>The cellular response to hormones is not static but is profoundly influenced by the cell's metabolic state and redox balance. These metabolic and oxidative conditions can modulate hormone signaling at multiple levels, from receptor function to downstream pathways, creating a dynamic interplay between hormonal regulation and cellular metabolism.</p>
          
          <ScientificDiagram 
            title="4. Metabolic-Hormonal Crosstalk and Redox Regulation" 
            caption="Illustration of the bidirectional relationship between cellular metabolism, redox state, and hormone signaling. A) Metabolic feedback mechanisms affecting hormone action. B) Redox modulation of hormone signaling pathways. C) Hormonal effects on metabolic processes and redox balance."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Metabolic-Hormonal Crosstalk and Redox Regulation</text>
                
                {/* A. Metabolic Feedback */}
                <g transform="translate(200, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. Metabolic Feedback</text>
                  
                  {/* Cell */}
                  <rect x="-100" y="-30" width="200" height="150" rx="5" fill="#F8F9F9" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Glucose */}
                  <rect x="-80" y="-15" width="40" height="15" rx="3" fill="#D2B4DE" />
                  <text x="-60" y="-5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Glucose</text>
                  
                  {/* ATP/ADP */}
                  <rect x="-20" y="-15" width="40" height="15" rx="3" fill="#D2B4DE" />
                  <text x="0" y="-5" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#8E44AD">ATP/ADP</text>
                  
                  {/* Insulin receptor */}
                  <rect x="-80" y="20" width="15" height="40" rx="2" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-72" y="45" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#8E44AD">IR</text>
                  
                  {/* Insulin signaling */}
                  <path d="M-65,40 L-40,40" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  <rect x="-40" y="30" width="20" height="20" rx="2" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-30" y="43" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#8E44AD">IRS</text>
                  
                  {/* K+ channel & Ca2+ */}
                  <rect x="0" y="30" width="20" height="20" rx="2" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="10" y="43" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#F1C40F">K+</text>
                  
                  <rect x="40" y="30" width="20" height="20" rx="2" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="50" y="43" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#F1C40F">Ca2+</text>
                  
                  {/* GLUT4 */}
                  <rect x="-80" y="80" width="40" height="15" rx="3" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-60" y="90" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">GLUT4</text>
                  
                  {/* Insulin exocytosis */}
                  <circle cx="50" cy="80" r="15" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="50" y="77" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#F1C40F">Insulin</text>
                  <text x="50" y="87" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#F1C40F">Release</text>
                  
                  {/* Connections */}
                  <path d="M-20,0 L0,30" stroke="#D2B4DE" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
                  <path d="M20,40 L40,40" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
                  <path d="M-30,50 L-60,80" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M50,50 L50,65" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
                </g>
                
                {/* B. Redox Modulation */}
                <g transform="translate(600, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. Redox Modulation</text>
                  
                  {/* Cell */}
                  <rect x="-100" y="-30" width="200" height="150" rx="5" fill="#F8F9F9" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* ROS source */}
                  <ellipse cx="-60" cy="-5" rx="25" ry="15" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="-60" y="-2" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">ROS</text>
                  
                  {/* NOX enzyme */}
                  <rect x="-10" y="-15" width="30" height="20" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="5" y="-2" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">NOX</text>
                  
                  {/* H2O2 */}
                  <circle cx="40" cy="-5" r="15" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="40" y="-2" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#E74C3C">H₂O₂</text>
                  
                  {/* Redox-sensitive PTPs */}
                  <rect x="-80" y="40" width="40" height="20" rx="3" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-60" y="50" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">PTP</text>
                  <text x="-60" y="60" textAnchor="middle" fontSize="6" fill="#3498DB">SH → SOH</text>
                  
                  {/* Kinase activation */}
                  <rect x="0" y="40" width="40" height="20" rx="3" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="20" y="53" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">JNK/p38</text>
                  
                  {/* NF-kB, Nrf2 */}
                  <rect x="-75" y="80" width="30" height="20" rx="3" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-60" y="93" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">NF-κB</text>
                  
                  <rect x="-25" y="80" width="30" height="20" rx="3" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-10" y="93" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">Nrf2</text>
                  
                  {/* Antioxidant genes */}
                  <rect x="20" y="80" width="60" height="20" rx="3" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="50" y="93" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">Antioxidants</text>
                  
                  {/* Connections */}
                  <path d="M20,5 L40,5" stroke="#E74C3C" strokeWidth="1.5" markerEnd="url(#redArrow)" />
                  <path d="M-35,-5 L-30,40" stroke="#E74C3C" strokeWidth="1.5" strokeDasharray="3,2" markerEnd="url(#blueArrow)" />
                  <path d="M-60,60 L-60,80" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M40,10 L20,40" stroke="#E74C3C" strokeWidth="1.5" strokeDasharray="3,2" markerEnd="url(#blueArrow)" />
                  <path d="M20,60 L-10,80" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M-10,100 L50,100" stroke="#3498DB" strokeWidth="1.5" strokeDasharray="3,2" markerEnd="url(#blueArrow)" />
                </g>
                
                {/* C. Hormonal Effects */}
                <g transform="translate(400, 375)">
                  {/* Background */}
                  <rect x="-320" y="-45" width="640" height="130" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111">C. Hormonal Effects on Metabolism & Redox</text>
                  
                  {/* Insulin effects */}
                  <g transform="translate(-200, 30)">
                    <rect x="-70" y="-30" width="140" height="60" rx="5" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="-12" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#27AE60">Insulin</text>
                    <text x="0" y="7" textAnchor="middle" fontSize="10" fill="#27AE60">↑ Glucose Uptake</text>
                    <text x="0" y="22" textAnchor="middle" fontSize="10" fill="#27AE60">↑ Glycogen Synthesis</text>
                  </g>
                  
                  {/* Cortisol effects */}
                  <g transform="translate(0, 30)">
                    <rect x="-70" y="-30" width="140" height="60" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                    <text x="0" y="-12" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#F1C40F">Cortisol</text>
                    <text x="0" y="7" textAnchor="middle" fontSize="10" fill="#F1C40F">↑ Gluconeogenesis</text>
                    <text x="0" y="22" textAnchor="middle" fontSize="10" fill="#F1C40F">↓ Glucose Utilization</text>
                  </g>
                  
                  {/* Thyroid effects */}
                  <g transform="translate(200, 30)">
                    <rect x="-70" y="-30" width="140" height="60" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="-12" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">Thyroid Hormones</text>
                    <text x="0" y="7" textAnchor="middle" fontSize="10" fill="#3498DB">↑ Basal Metabolic Rate</text>
                    <text x="0" y="22" textAnchor="middle" fontSize="10" fill="#3498DB">↑ Mitochondrial Activity</text>
                  </g>
                </g>
                
                {/* Bidirectional arrows */}
                <path d="M300,230 L400,330" stroke="#333" strokeWidth="2" markerEnd="url(#blackArrow)" />
                <path d="M600,230 L500,330" stroke="#333" strokeWidth="2" markerEnd="url(#blackArrow)" />
                <path d="M300,250 L400,300" stroke="#333" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#blackArrow)" />
                <path d="M600,250 L500,300" stroke="#333" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#blackArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60" />
                  </marker>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                  <marker id="yellowArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#F1C40F" />
                  </marker>
                  <marker id="redArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C" />
                  </marker>
                  <marker id="blackArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#333333" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Metabolic Influences on Hormone Action</h3>
              <p>The cellular response to hormones is modulated by the metabolic state, including substrate availability (glucose, fatty acids), energy charge (ATP/ADP ratio), and cofactor levels (NAD+/NADH). For example, pancreatic β-cells secrete insulin in proportion to blood glucose levels because glucose metabolism increases the cellular ATP/ADP ratio, which closes ATP-sensitive K+ channels, depolarizes the cell, and triggers calcium-mediated insulin exocytosis. This mechanism directly couples hormone secretion to metabolic sensing.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Redox Regulation of Signaling Pathways</h3>
              <p>Cellular redox state profoundly influences hormone signaling pathways through reversible oxidation of cysteine residues in key signaling proteins. Reactive oxygen species (ROS) can enhance insulin signaling by inhibiting protein tyrosine phosphatases (PTPs) through oxidation of their catalytic cysteine residues. However, chronic oxidative stress caused by metabolic overload (excess nutrients, mitochondrial dysfunction) can activate stress kinases (JNK, IKKβ, p38 MAPK) that impair insulin receptor signaling, contributing to insulin resistance.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Hormonal Regulation of Metabolism</h3>
              <p>Hormones orchestrate whole-body metabolism by regulating substrate utilization, energy expenditure, and storage. Insulin promotes glucose uptake in muscle and fat while suppressing glucose production in the liver. Conversely, counter-regulatory hormones (glucagon, cortisol, epinephrine) increase blood glucose during fasting or stress. Thyroid hormones increase basal metabolic rate and mitochondrial activity, while adipokines (leptin, adiponectin) communicate information about energy stores to the brain and peripheral tissues.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Redox Signaling as a Second Dimension</h3>
              <p>Redox signaling can act as a "second dimension" of hormone action, ensuring that cellular responses are appropriate for the current oxidative and metabolic state. Interestingly, many hormones themselves influence redox balance—thyroid hormones increase mitochondrial ROS production as a byproduct of accelerated metabolism, while insulin has antioxidant effects through activation of Nrf2 and suppression of FOXO transcription factors. This reciprocal relationship creates complex feedback loops between hormonal systems and redox networks.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The integration of metabolic sensing, redox signaling, and hormone action creates a sophisticated regulatory network that adapts cellular function to changing physiological demands. In metabolic disorders like diabetes and obesity, disruption of these interconnected systems can lead to a vicious cycle where hormonal resistance, metabolic dysfunction, and oxidative stress mutually reinforce each other. Understanding these relationships provides insights into the pathophysiology of metabolic diseases and suggests therapeutic approaches that target the underlying metabolic-hormonal-redox imbalances.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 5,
      title: "Key Hormones in Chronic Disease",
      content: (
        <>
          <p>Hormonal imbalances play a central role in the pathogenesis and progression of many chronic diseases. Understanding how key hormones contribute to these conditions provides insights into potential therapeutic targets and strategies for prevention and management.</p>
          
          <ScientificDiagram 
            title="5. Insulin Resistance and Metabolic Syndrome" 
            caption="Molecular mechanisms and systemic consequences of insulin resistance. A) Normal insulin signaling pathway in metabolic tissues. B) Disruptions in insulin signaling that lead to insulin resistance. C) Metabolic and clinical manifestations of insulin resistance and its progression to metabolic syndrome and type 2 diabetes."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Insulin Resistance and Metabolic Syndrome</text>
                
                {/* A. Normal Insulin Signaling */}
                <g transform="translate(200, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#27AE60">A. Normal Insulin Signaling</text>
                  
                  {/* Cell */}
                  <rect x="-100" y="-30" width="200" height="150" rx="5" fill="#F8F9F9" stroke="#27AE60" strokeWidth="1.5" />
                  
                  {/* Insulin */}
                  <circle cx="-80" cy="-40" r="10" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="-80" y="-37" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#27AE60">Insulin</text>
                  
                  {/* Insulin receptor */}
                  <rect x="-80" y="-30" width="10" height="30" rx="2" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="-75" y="-10" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#27AE60">IR</text>
                  
                  {/* IRS */}
                  <rect x="-50" y="10" width="30" height="15" rx="3" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="-35" y="20" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#27AE60">IRS</text>
                  
                  {/* PI3K */}
                  <rect x="0" y="10" width="30" height="15" rx="3" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="15" y="20" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#27AE60">PI3K</text>
                  
                  {/* AKT */}
                  <rect x="50" y="10" width="30" height="15" rx="3" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="65" y="20" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#27AE60">AKT</text>
                  
                  {/* GLUT4 Vesicle */}
                  <circle cx="0" cy="60" r="15" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="0" y="63" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#27AE60">GLUT4</text>
                  
                  {/* GLUT4 translocation */}
                  <rect x="-80" y="100" width="40" height="15" rx="3" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="-60" y="110" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#27AE60">GLUT4</text>
                  
                  {/* Glucose uptake */}
                  <circle cx="-30" cy="110" r="8" fill="#FCF3CF" />
                  <text x="-30" y="113" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#F1C40F">Glucose</text>
                  
                  {/* Glycogen synthesis */}
                  <rect x="30" y="85" width="60" height="15" rx="3" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="60" y="95" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#27AE60">Glycogen Synthesis</text>
                  
                  {/* Lipogenesis */}
                  <rect x="30" y="110" width="60" height="15" rx="3" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="60" y="120" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#27AE60">Lipogenesis</text>
                  
                  {/* Arrows */}
                  <path d="M-75,-40 L-75,-30" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                  <path d="M-75,0 L-50,10" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                  <path d="M-20,17 L0,17" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                  <path d="M30,17 L50,17" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                  <path d="M65,25 L20,55" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                  <path d="M0,75 L-60,100" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                  <path d="M65,25 L60,85" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                </g>
                
                {/* B. Insulin Resistance */}
                <g transform="translate(600, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#E74C3C">B. Insulin Resistance</text>
                  
                  {/* Cell */}
                  <rect x="-100" y="-30" width="200" height="150" rx="5" fill="#F8F9F9" stroke="#E74C3C" strokeWidth="1.5" />
                  
                  {/* Excessive insulin */}
                  <circle cx="-80" cy="-40" r="10" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="-80" y="-37" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">Insulin</text>
                  <circle cx="-65" cy="-45" r="10" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="-65" y="-42" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">Insulin</text>
                  
                  {/* Downregulated insulin receptor */}
                  <rect x="-80" y="-30" width="10" height="30" rx="2" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="-75" y="-10" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">IR</text>
                  <text x="-75" y="5" textAnchor="middle" fontSize="10" fill="#E74C3C">↓</text>
                  
                  {/* IRS with inhibitory phosphorylation */}
                  <rect x="-50" y="10" width="30" height="15" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="-35" y="20" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">IRS</text>
                  <circle cx="-50" cy="5" r="8" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1" />
                  <text x="-50" y="8" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#E74C3C">P</text>
                  <text x="-50" y="16" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#E74C3C">Ser</text>
                  
                  {/* Impaired signaling */}
                  <rect x="0" y="10" width="30" height="15" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="15" y="20" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">PI3K</text>
                  <text x="15" y="35" textAnchor="middle" fontSize="10" fill="#E74C3C">↓</text>
                  
                  <rect x="50" y="10" width="30" height="15" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="65" y="20" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">AKT</text>
                  <text x="65" y="35" textAnchor="middle" fontSize="10" fill="#E74C3C">↓</text>
                  
                  {/* Reduced GLUT4 translocation */}
                  <circle cx="0" cy="60" r="15" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="0" y="63" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">GLUT4</text>
                  <text x="25" y="63" textAnchor="middle" fontSize="12" fill="#E74C3C">↓</text>
                  
                  {/* Inflammatory factors */}
                  <rect x="-90" y="50" width="30" height="15" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="-75" y="60" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">JNK</text>
                  
                  <rect x="-90" y="75" width="30" height="15" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="-75" y="85" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">IKKβ</text>
                  
                  {/* Reduced glucose uptake */}
                  <rect x="-80" y="110" width="40" height="15" rx="3" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1" strokeDasharray="3,1" />
                  <text x="-60" y="120" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">↓</text>
                  
                  {/* Metabolic consequences */}
                  <rect x="0" y="100" width="90" height="40" rx="3" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="45" y="115" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">↑ Hepatic Glucose Output</text>
                  <text x="45" y="130" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#E74C3C">↑ Lipolysis</text>
                  
                  {/* Arrows */}
                  <path d="M-75,5 L-55,10" stroke="#E74C3C" strokeWidth="1.5" strokeDasharray="3,1" markerEnd="url(#redArrow)" />
                  <path d="M-75,60 L-55,25" stroke="#E74C3C" strokeWidth="1.5" markerEnd="url(#redArrow)" />
                  <path d="M-75,75 L-55,25" stroke="#E74C3C" strokeWidth="1.5" markerEnd="url(#redArrow)" />
                </g>
                
                {/* C. Metabolic Syndrome */}
                <g transform="translate(400, 360)">
                  {/* Background */}
                  <rect x="-320" y="-50" width="640" height="140" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111">C. Metabolic Syndrome and Type 2 Diabetes</text>
                  
                  {/* Clinical consequences */}
                  <g transform="translate(-220, 30)">
                    <rect x="-60" y="-20" width="120" height="65" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Hyperinsulinemia</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="10" fill="#F1C40F">β-cell compensation</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#F1C40F">↑↑ Insulin secretion</text>
                  </g>
                  
                  <g transform="translate(-70, 30)">
                    <rect x="-60" y="-20" width="120" height="65" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Hypertension</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="10" fill="#F1C40F">↑ Na+ retention</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#F1C40F">↑ Sympathetic activity</text>
                  </g>
                  
                  <g transform="translate(80, 30)">
                    <rect x="-60" y="-20" width="120" height="65" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Dyslipidemia</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="10" fill="#F1C40F">↑ Triglycerides</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#F1C40F">↓ HDL-C</text>
                  </g>
                  
                  <g transform="translate(230, 30)">
                    <rect x="-60" y="-20" width="120" height="65" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Type 2 Diabetes</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="10" fill="#F1C40F">β-cell failure</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="10" fill="#F1C40F">Hyperglycemia</text>
                  </g>
                </g>
                
                {/* Connecting arrows */}
                <path d="M200,200 L400,310" stroke="#333" strokeWidth="2" markerEnd="url(#blackArrow)" />
                <path d="M600,200 L500,310" stroke="#333" strokeWidth="2" markerEnd="url(#blackArrow)" />
                <path d="M390,430 L475,430" stroke="#333" strokeWidth="2" markerEnd="url(#blackArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60" />
                  </marker>
                  <marker id="redArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C" />
                  </marker>
                  <marker id="blackArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#333333" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Insulin: Metabolic Control and Insulin Resistance</h3>
              <p>Insulin is the master regulator of nutrient storage and utilization, facilitating glucose uptake into muscle and fat, promoting glycogen and fat synthesis, and inhibiting catabolic processes. Insulin resistance—a state in which target cells have a diminished response to normal insulin levels—is the primary driver of metabolic syndrome and type 2 diabetes. It develops through multiple mechanisms, including receptor downregulation, inhibitory serine phosphorylation of insulin receptor substrates (IRS), increased activity of protein tyrosine phosphatases, and inflammation-activated stress kinases.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Cortisol: Stress Response and Metabolic Effects</h3>
              <p>Cortisol, produced by the adrenal cortex, coordinates the body's response to stress by mobilizing energy resources, suppressing inflammation, and modulating immune function. While essential for survival, chronic cortisol elevation (as in Cushing's syndrome or chronic stress) promotes central obesity, insulin resistance, and hypertension—a constellation resembling metabolic syndrome. Cortisol achieves these effects by increasing hepatic glucose production, antagonizing insulin action, promoting visceral fat accumulation, and stimulating appetite.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Thyroid Hormones: Metabolic Regulation</h3>
              <p>Thyroid hormones (T3 and T4) regulate basal metabolic rate, thermogenesis, and numerous physiological processes. Hypothyroidism can contribute to metabolic dysfunction through reduced energy expenditure, dyslipidemia (elevated LDL-C, triglycerides), and impaired glucose metabolism. Conversely, hyperthyroidism accelerates metabolism but can paradoxically worsen insulin resistance through increased lipolysis and gluconeogenesis. The optimal thyroid function for metabolic health appears to be within the normal range, with subclinical deviations potentially contributing to cardiometabolic risk.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Sex Hormones: Metabolic and Cardiovascular Effects</h3>
              <p>Sex hormones significantly influence metabolic health and disease risk. Estrogens generally promote favorable metabolic profiles in women, reducing visceral fat deposition and enhancing insulin sensitivity. The decline of estrogen during menopause contributes to increased visceral adiposity and cardiometabolic risk. In men, testosterone deficiency is associated with metabolic syndrome features (central obesity, insulin resistance, dyslipidemia), while excess androgens in women (as in polycystic ovary syndrome) can similarly impair metabolic health through mechanisms involving adipocyte function and insulin signaling.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The interrelationships between insulin resistance, cortisol dysregulation, thyroid function, and sex hormone balance highlight the complex hormonal underpinnings of metabolic syndrome and related disorders. These hormones do not act in isolation but form an integrated network that regulates energy homeostasis across multiple tissues. Therapeutic approaches for metabolic disorders increasingly recognize this hormonal complexity, targeting not only insulin resistance but also addressing cortisol excess, optimizing thyroid function, and considering sex hormone influences. Lifestyle interventions such as physical activity, stress management, and healthy nutrition remain foundational treatments that beneficially impact multiple hormonal axes simultaneously.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 6,
      title: "Clinical Applications and Therapeutic Approaches",
      content: (
        <>
          <p>Understanding the cellular and molecular mechanisms of hormone action provides the foundation for developing effective diagnostic tools and therapeutic strategies for hormonal disorders. This systems-based approach allows clinicians to address not only the primary hormonal imbalance but also the downstream effects and feedback mechanisms that contribute to disease pathophysiology.</p>
          
          <ScientificDiagram 
            title="6. Hormone Assessment and Therapeutic Interventions" 
            caption="Comprehensive overview of approaches to evaluating and treating hormonal disorders. A) Modern diagnostic methods for hormone assessment, including considerations of bioavailability and tissue sensitivity. B) Pharmacological interventions targeting hormone signaling at multiple levels. C) Lifestyle and nutritional approaches that modulate hormone function."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Hormone Assessment and Therapeutic Interventions</text>
                
                {/* A. Diagnostic Approaches */}
                <g transform="translate(200, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. Diagnostic Approaches</text>
                  
                  {/* Serum levels */}
                  <rect x="-100" y="-20" width="80" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-60" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Serum Levels</text>
                  
                  {/* Free hormone */}
                  <rect x="-100" y="20" width="80" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-60" y="35" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Free Hormone</text>
                  
                  {/* Static vs. Dynamic */}
                  <rect x="0" y="-20" width="80" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="40" y="-12" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Static vs.</text>
                  <text x="40" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Dynamic Tests</text>
                  
                  {/* Metabolites */}
                  <rect x="0" y="20" width="80" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="40" y="35" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Metabolites</text>
                  
                  {/* Tissue sensitivity */}
                  <rect x="-100" y="70" width="180" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-10" y="85" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Receptor Function/Sensitivity</text>
                  
                  {/* Diurnal rhythm */}
                  <rect x="-100" y="110" width="80" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-60" y="125" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Diurnal Patterns</text>
                  
                  {/* Genetic testing */}
                  <rect x="0" y="110" width="80" height="30" rx="5" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="40" y="125" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Genetic Testing</text>
                </g>
                
                {/* B. Pharmacological Approaches */}
                <g transform="translate(600, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. Pharmacological Approaches</text>
                  
                  {/* Hormone replacement */}
                  <rect x="-100" y="-20" width="80" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-60" y="-12" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Hormone</text>
                  <text x="-60" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Replacement</text>
                  
                  {/* Receptor modulators */}
                  <rect x="0" y="-20" width="80" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="40" y="-12" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Receptor</text>
                  <text x="40" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Modulators</text>
                  
                  {/* Enzyme inhibitors */}
                  <rect x="-100" y="20" width="80" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-60" y="28" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Enzyme</text>
                  <text x="-60" y="40" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Inhibitors</text>
                  
                  {/* Signal transducers */}
                  <rect x="0" y="20" width="80" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="40" y="28" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Signal</text>
                  <text x="40" y="40" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Transducers</text>
                  
                  {/* Examples */}
                  <rect x="-100" y="60" width="80" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-60" y="75" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Metformin</text>
                  
                  <rect x="0" y="60" width="80" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="40" y="75" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">GLP-1 RAs</text>
                  
                  <rect x="-100" y="100" width="80" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-60" y="115" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">Thiazolidinediones</text>
                  
                  <rect x="0" y="100" width="80" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="40" y="115" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">SGLT2 Inhibitors</text>
                </g>
                
                {/* C. Lifestyle Approaches */}
                <g transform="translate(400, 375)">
                  {/* Background */}
                  <rect x="-320" y="-45" width="640" height="130" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111">C. Lifestyle and Nutritional Approaches</text>
                  
                  {/* Exercise */}
                  <g transform="translate(-225, 30)">
                    <rect x="-50" y="-20" width="100" height="80" rx="5" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#27AE60">Exercise</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="9" fill="#27AE60">↑ Insulin Sensitivity</text>
                    <text x="0" y="30" textAnchor="middle" fontSize="9" fill="#27AE60">↑ Glucose Transport</text>
                    <text x="0" y="45" textAnchor="middle" fontSize="9" fill="#27AE60">↓ Inflammation</text>
                  </g>
                  
                  {/* Nutrition */}
                  <g transform="translate(-75, 30)">
                    <rect x="-50" y="-20" width="100" height="80" rx="5" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#27AE60">Nutrition</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="9" fill="#27AE60">Mediterranean Diet</text>
                    <text x="0" y="30" textAnchor="middle" fontSize="9" fill="#27AE60">Time-Restricted Feeding</text>
                    <text x="0" y="45" textAnchor="middle" fontSize="9" fill="#27AE60">Anti-Inflammatory Foods</text>
                  </g>
                  
                  {/* Stress Management */}
                  <g transform="translate(75, 30)">
                    <rect x="-50" y="-20" width="100" height="80" rx="5" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#27AE60">Stress Management</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="9" fill="#27AE60">↓ Cortisol</text>
                    <text x="0" y="30" textAnchor="middle" fontSize="9" fill="#27AE60">Mindfulness</text>
                    <text x="0" y="45" textAnchor="middle" fontSize="9" fill="#27AE60">Improved Sleep Quality</text>
                  </g>
                  
                  {/* Targeted Supplements */}
                  <g transform="translate(225, 30)">
                    <rect x="-50" y="-20" width="100" height="80" rx="5" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="-5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#27AE60">Supplements</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="9" fill="#27AE60">Vitamin D</text>
                    <text x="0" y="30" textAnchor="middle" fontSize="9" fill="#27AE60">Magnesium</text>
                    <text x="0" y="45" textAnchor="middle" fontSize="9" fill="#27AE60">Omega-3 Fatty Acids</text>
                  </g>
                </g>
                
                {/* Connecting arrows */}
                <path d="M300,200 L400,330" stroke="#333" strokeWidth="2" markerEnd="url(#blackArrow)" />
                <path d="M500,200 L400,330" stroke="#333" strokeWidth="2" markerEnd="url(#blackArrow)" />
                
                {/* Integration circle */}
                <circle cx="400" cy="280" r="30" fill="#f9f9f9" stroke="#333" strokeWidth="2" />
                <text x="400" y="275" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Integrated</text>
                <text x="400" y="290" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Approach</text>
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="blackArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#333333" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Advanced Hormone Assessment</h3>
              <p>Modern endocrine evaluation extends beyond simple measurement of total hormone levels to assess the functional hormonal status. This includes evaluation of free (bioactive) hormone levels, hormone transport proteins, metabolites, and dynamic testing to evaluate feedback mechanisms. New techniques like liquid chromatography-mass spectrometry (LC-MS/MS) provide greater specificity and sensitivity, particularly for steroid hormones. Additionally, genetic testing for receptor polymorphisms or metabolic enzymes can identify individuals with altered hormone sensitivity or metabolism.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Pharmacological Strategies</h3>
              <p>Pharmaceutical interventions for hormonal disorders can target multiple levels of hormone action. These include direct hormone replacement (e.g., levothyroxine for hypothyroidism), selective receptor modulators (e.g., SERMs for estrogen receptors), enzyme inhibitors affecting hormone synthesis or metabolism (e.g., aromatase inhibitors), and modulators of post-receptor signaling pathways. Novel approaches include biologics like GLP-1 receptor agonists, which not only regulate glucose metabolism but also influence appetite centers through central effects, highlighting the interconnectedness of hormonal systems.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Lifestyle Medicine Approaches</h3>
              <p>Lifestyle interventions powerfully influence hormone function, often addressing the root causes of hormonal imbalances. Regular physical activity enhances insulin sensitivity through multiple mechanisms, including increased GLUT4 translocation and improved mitochondrial function. Dietary patterns like the Mediterranean diet or time-restricted feeding can modulate insulin, leptin, and inflammatory cytokines. Stress management through mindfulness, yoga, or other techniques can normalize cortisol patterns and mitigate their metabolic consequences. Adequate sleep is increasingly recognized as crucial for hormonal homeostasis, affecting insulin sensitivity, appetite regulators, and reproductive hormones.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Integrative Approach to Complex Hormonal Disorders</h3>
              <p>Complex conditions like polycystic ovary syndrome (PCOS) and metabolic syndrome require multi-level interventions addressing their hormonal complexity. An integrative approach might combine metformin (enhancing insulin sensitivity), lifestyle modifications (diet, exercise), targeted supplements (e.g., vitamin D, inositol), and specific hormonal treatments as needed. This comprehensive strategy acknowledges that hormonal disorders typically involve multiple feedback loops and interconnected pathways rather than a single defect. The goal is to restore hormonal balance not through simple replacement but by reestablishing the complex network of signaling relationships that maintain homeostasis.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The modern approach to hormonal disorders embraces their systems biology nature, recognizing that effective treatment often requires coordinated interventions at multiple levels. Precision endocrinology aims to individualize therapy based on specific molecular mechanisms, genetic factors, and environmental influences that contribute to an individual's hormonal imbalance. As our understanding of hormone action at the cellular and molecular level continues to advance, therapeutic strategies will increasingly target specific nodes within hormonal signaling networks, leading to more effective and personalized interventions for conditions ranging from diabetes to reproductive disorders.
          </KeyTakeaway>
        </>
      )
    }
  ];

  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-2">Hormones: Cellular Fundamentals</h1>
              <p className="text-lg text-gray-600">A Systems and Clinical Perspective</p>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-3 py-1 rounded-full flex items-center text-sm mt-2 md:mt-0">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              <span>Mentorship Content</span>
            </div>
          </div>
          
          <Separator className="mb-6" />
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              <Slideshow slides={lectureSlides} />
              
              {/* Video Embed */}
              <div className="mt-8">
                <h3 className="text-lg font-medium mb-4">Lecture Video</h3>
                <div className="flex justify-center">
                  <iframe 
                    src="https://share.descript.com/embed/NKzvpxseYNo" 
                    width="640" 
                    height="360" 
                    frameBorder="0" 
                    allowFullScreen
                    className="rounded-lg shadow-sm"
                    title="Hormones: Cellular Fundamentals - Lecture Video"
                  />
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <Card className="p-4 sticky top-20">
                <h3 className="text-lg font-medium mb-4">Resources</h3>
                
                <div className="space-y-3 mb-6">
                  {resources.map((resource, i) => (
                    <a 
                      key={i}
                      href={resource.link}
                      className="flex items-center p-2 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      {resource.icon}
                      <span className="ml-2 text-sm">{resource.title}</span>
                    </a>
                  ))}
                </div>
                
                <h3 className="text-lg font-medium mb-4">Slide Topics</h3>
                
                <nav className="space-y-1">
                  {lectureSlides.map((slide) => (
                    <div
                      key={slide.id}
                      className="block px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors cursor-default"
                    >
                      {slide.title}
                    </div>
                  ))}
                </nav>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}

export default HormonesCellularFundamentals;